// A minimal .xlsx writer: a handful of worksheets of plain values, nothing
// else. The Batches export is two sheets (§7k.7) and there is no spreadsheet
// library in this build, so this writes the OOXML parts by hand into a zip
// with every entry stored (method 0), which every spreadsheet reader accepts.
// Strings are inline (no shared-string table); numbers are numbers, so a sum
// in the receiving spreadsheet works without converting a column first.

export type CellValue = string | number | null | undefined

export interface Sheet {
  name: string
  rows: CellValue[][]
  // Column widths in characters, from column A.
  widths?: number[]
  // Columns (from 0) formatted as text, so what someone types into them in
  // Excel later stays as typed: "0042" is not turned into the number 42.
  text?: number[]
  // Locked against editing, with no password: it guides whoever fills the
  // workbook in and guards nothing (the Site import judges every cell again).
  protect?: boolean
  validations?: Validation[]
}

// A cell rule Excel applies as the workbook is filled in: a dropdown drawn
// from a range, or a number between two limits.
export interface Validation {
  // The cells, like "E2:E5001".
  sqref: string
  // A range to choose from, like "Varieties!$B$2:$B$9".
  list?: string
  whole?: [number, number]
  decimal?: [number, number]
  error?: string
}

const enc = new TextEncoder()

const CRC_TABLE = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
})()

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // Control characters other than tab/newline are not legal XML 1.0.
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')

// Column letters: 0 → A, 25 → Z, 26 → AA.
export function colName(i: number): string {
  let s = ''
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s
  return s
}

function validationXml(v: Validation): string {
  const error = v.error ? ` errorTitle="Not allowed" error="${esc(v.error)}"` : ''
  const open = (type: string, extra = '') => `<dataValidation type="${type}"${extra} allowBlank="1" showErrorMessage="1"${error} sqref="${esc(v.sqref)}">`
  if (v.list) return `${open('list')}<formula1>${esc(v.list)}</formula1></dataValidation>`
  const range = v.whole || v.decimal
  if (!range) return ''
  return `${open(v.whole ? 'whole' : 'decimal', ' operator="between"')}<formula1>${range[0]}</formula1><formula2>${range[1]}</formula2></dataValidation>`
}

function sheetXml(sheet: Sheet): string {
  const { rows } = sheet
  const body = rows
    .map((row, r) => {
      const cells = row
        .map((v, c) => {
          const ref = colName(c) + (r + 1)
          // The first row is the header, drawn bold by style 1; a text
          // column's cells are style 2.
          const st = r === 0 ? ' s="1"' : sheet.text?.includes(c) ? ' s="2"' : ''
          if (v === null || v === undefined || v === '') return ''
          if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${ref}"${st}><v>${v}</v></c>`
          return `<c r="${ref}"${st} t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`
        })
        .join('')
      return `<row r="${r + 1}">${cells}</row>`
    })
    .join('')
  // The parts in the order the schema requires: views, columns, data,
  // protection, validations.
  const ncols = Math.max(sheet.widths?.length || 0, ...(sheet.text || []).map((c) => c + 1))
  const col = (i: number) => {
    const w = sheet.widths?.[i]
    const width = w ? ` width="${w}" customWidth="1"` : ''
    const style = sheet.text?.includes(i) ? ' style="2"' : ''
    return width || style ? `<col min="${i + 1}" max="${i + 1}"${width || ' width="9.140625"'}${style}/>` : ''
  }
  const cols = ncols ? `<cols>${Array.from({ length: ncols }, (_, i) => col(i)).join('')}</cols>` : ''
  const protect = sheet.protect ? '<sheetProtection sheet="1" objects="1" scenarios="1"/>' : ''
  const rules = (sheet.validations || []).map(validationXml).filter(Boolean)
  const validations = rules.length ? `<dataValidations count="${rules.length}">${rules.join('')}</dataValidations>` : ''
  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>' +
    `${cols}<sheetData>${body}</sheetData>${protect}${validations}</worksheet>`
  )
}

// Sheet names: at most 31 characters, none of []:*?/\ (the spreadsheet rule).
const sheetName = (s: string) => s.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31) || 'Sheet'

function parts(sheets: Sheet[]): [string, string][] {
  const n = sheets.length
  const idx = sheets.map((_, i) => i + 1)
  return [
    [
      '[Content_Types].xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        idx.map((i) => `<Override PartName="/xl/worksheets/sheet${i}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('') +
        '</Types>',
    ],
    [
      '_rels/.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    ],
    [
      'xl/workbook.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        '<sheets>' +
        sheets.map((s, i) => `<sheet name="${esc(sheetName(s.name))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
        '</sheets></workbook>',
    ],
    [
      'xl/_rels/workbook.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        idx.map((i) => `<Relationship Id="rId${i}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i}.xml"/>`).join('') +
        `<Relationship Id="rId${n + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
        '</Relationships>',
    ],
    [
      'xl/styles.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        '<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
        '<xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>' +
        '</styleSheet>',
    ],
    ...sheets.map((s, i) => [`xl/worksheets/sheet${i + 1}.xml`, sheetXml(s)] as [string, string]),
  ]
}

// A zip of stored entries. The timestamp is fixed (1980-01-01) so the same
// data always produces the same bytes.
// Returned over a plain ArrayBuffer so it is a valid BlobPart as it stands.
export function zipStored(files: [string, Uint8Array][]): Uint8Array<ArrayBuffer> {
  const chunks: Uint8Array[] = []
  const central: Uint8Array[] = []
  let offset = 0
  const u16 = (v: DataView, at: number, n: number) => v.setUint16(at, n, true)
  const u32 = (v: DataView, at: number, n: number) => v.setUint32(at, n, true)
  for (const [name, data] of files) {
    const nb = enc.encode(name)
    const crc = crc32(data)
    const head = new Uint8Array(30 + nb.length)
    const h = new DataView(head.buffer)
    u32(h, 0, 0x04034b50)
    u16(h, 4, 20) // version needed
    u16(h, 6, 0x0800) // UTF-8 names
    u16(h, 8, 0) // stored
    u16(h, 10, 0) // time
    u16(h, 12, 0x0021) // date 1980-01-01
    u32(h, 14, crc)
    u32(h, 18, data.length)
    u32(h, 22, data.length)
    u16(h, 26, nb.length)
    u16(h, 28, 0)
    head.set(nb, 30)
    const cd = new Uint8Array(46 + nb.length)
    const c = new DataView(cd.buffer)
    u32(c, 0, 0x02014b50)
    u16(c, 4, 20)
    u16(c, 6, 20)
    u16(c, 8, 0x0800)
    u16(c, 10, 0)
    u16(c, 12, 0)
    u16(c, 14, 0x0021)
    u32(c, 16, crc)
    u32(c, 20, data.length)
    u32(c, 24, data.length)
    u16(c, 28, nb.length)
    u32(c, 42, offset)
    cd.set(nb, 46)
    chunks.push(head, data)
    central.push(cd)
    offset += head.length + data.length
  }
  const cdSize = central.reduce((a, x) => a + x.length, 0)
  const end = new Uint8Array(22)
  const e = new DataView(end.buffer)
  u32(e, 0, 0x06054b50)
  u16(e, 8, files.length)
  u16(e, 10, files.length)
  u32(e, 12, cdSize)
  u32(e, 16, offset)
  const all = [...chunks, ...central, end]
  const out = new Uint8Array(all.reduce((a, x) => a + x.length, 0))
  let at = 0
  for (const x of all) {
    out.set(x, at)
    at += x.length
  }
  return out
}

export function workbook(sheets: Sheet[]): Uint8Array<ArrayBuffer> {
  return zipStored(parts(sheets).map(([name, xml]) => [name, enc.encode(xml)]))
}

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
